
import React, { useState, useEffect } from 'react';
import { ROLE_CATEGORIES, STUDENT_SPECIALIZATIONS, THERAPIST_SPECIALTIES, WESTERN_MEDICINE_SPECIALTIES } from '../constants';
import { District, Gender, Registrant, EventOption } from '../types';
import { ToastType } from './Toast';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  Send, Info, Mail, User, Phone, MapPin,
  Stethoscope, CheckCircle, ArrowRight, ArrowLeft,
  Calendar, Save, Trash2, Loader2, AlertTriangle, Ban
} from 'lucide-react';
import { isDeadlinePassed, getDeadlineStatus } from '../utils/dateUtils';
import {
  formatOfficialSupportAvailability,
  getOfficialSupportDayOptions,
  isOfficialSupportEvent,
  isOfficialSupportRole
} from '../utils/officialSupport';
import {
  sanitizeEmail,
  sanitizeName,
  sanitizePhone,
  sanitizeChurch,
  sanitizeMessage,
  sanitizeRoleDescription,
  validateGender,
  validateDistrict,
  validateRole,
  validateEvents
} from '../utils/sanitize';

interface RegistrationFormProps {
  events: EventOption[];
  year: number;
  existingRegistrations?: Registrant[];
  onSubmit: (data: Omit<Registrant, 'id' | 'submissionDate'>) => void | Promise<void>;
  showToast: (type: ToastType, title: string, message?: string) => void;
  preSelectedEventId?: string; // Optional pre-selected event from clicking event card
  skipDuplicateCheck?: boolean;
}

const STEPS = [
  { id: 1, title: '基本資料', icon: User },
  { id: 2, title: '醫宣角色', icon: Stethoscope },
  { id: 3, title: '選擇梯隊', icon: Calendar },
  { id: 4, title: '確認送出', icon: CheckCircle },
];

const STORAGE_KEY = 'tjc_registration_draft_v1';

const RegistrationForm: React.FC<RegistrationFormProps> = ({ events, year, onSubmit, showToast, preSelectedEventId, skipDuplicateCheck = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isExiting, setIsExiting] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [hasDraft, setHasDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [_existingRegistration, _setExistingRegistration] = useState<Registrant | null>(null);
  const [_isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const preSelectedProcessedRef = React.useRef<string | null>(null);

  const initialData = {
    email: '',
    name: '',
    gender: Gender.Male,
    church: '',
    district: District.North,
    phone: '',
    role: '',
    roles: [] as string[],
    otherRoleDescription: '',
    studentSpecialization: '',
    westernMedicineSpecialty: '',
    therapistSpecialty: '',
    canProvideOfficialSupport: undefined as boolean | undefined,
    officialSupportAvailability: {} as Record<string, string[]>,
    message: '',
    selectedEvents: [] as string[]
  };

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const hasOfficialSupportRole = (formData.roles || []).some(isOfficialSupportRole);
  const selectedEvents = formData.selectedEvents
    .map(eventId => events.find(event => event.id === eventId))
    .filter((event): event is EventOption => Boolean(event));
  const selectedSupportEvents = selectedEvents.filter(isOfficialSupportEvent);
  const requiresOfficialSupport = hasOfficialSupportRole && selectedSupportEvents.length > 0;

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        // 🚀 檢查草稿是否過期（7天後自動清除）
        const DRAFT_EXPIRY_DAYS = 7;
        const expiryTime = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // 7天（毫秒）

        if (parsed.timestamp) {
          const age = Date.now() - parsed.timestamp;

          if (age > expiryTime) {
            // 草稿已過期，清除並提示用戶
            localStorage.removeItem(STORAGE_KEY);
            showToast('info', '草稿已過期', `您的草稿已超過 ${DRAFT_EXPIRY_DAYS} 天，已自動清除。請重新填寫報名資料。`);
            return;
          }
        }

        // 草稿有效，載入資料
        const { timestamp, ...draftData } = parsed; // 移除 timestamp 欄位
        if (draftData.name || draftData.email || draftData.phone) {
          // 保留聯絡資料，只帶入本年度仍存在的梯次與報備日期。
          const draftEventIds = Array.isArray(draftData.selectedEvents)
            ? draftData.selectedEvents.filter((id: string) => events.some(event => event.id === id))
            : [];
          // 草稿還原時一併保留本次點選的梯次，避免初始化再次執行時覆蓋預選。
          if (preSelectedEventId && events.some(event => event.id === preSelectedEventId)
            && !draftEventIds.includes(preSelectedEventId)) {
            draftEventIds.push(preSelectedEventId);
          }
          const draftAvailability = Object.fromEntries(
            Object.entries(draftData.officialSupportAvailability || {})
              .filter(([id]) => draftEventIds.includes(id))
          );
          setHasDraft(true);
          setFormData({
            ...initialData,
            ...draftData,
            selectedEvents: draftEventIds,
            officialSupportAvailability: draftAvailability,
          });

          // 顯示草稿年齡
          const daysOld = parsed.timestamp
            ? Math.floor((Date.now() - parsed.timestamp) / (24 * 60 * 60 * 1000))
            : 0;
          const ageText = daysOld > 0 ? `（${daysOld} 天前）` : '（今天）';

          showToast('info', '已載入草稿', `已載入上次未完成的資料 ${ageText}`);
        }
      } catch (e) {
        console.error("Failed to load draft", e);
        // 如果解析失敗，清除損壞的草稿
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (JSON.stringify(formData) !== JSON.stringify(initialData)) {
        // 🚀 儲存草稿時加入時間戳，用於過期檢查
        const draftWithTimestamp = {
          ...formData,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draftWithTimestamp));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Auto-select pre-selected event from event card click
  useEffect(() => {
    // Only process if we have a preSelectedEventId and haven't processed it yet
    if (preSelectedEventId && preSelectedEventId !== preSelectedProcessedRef.current) {
      const selectedEvent = events.find(e => e.id === preSelectedEventId);
      
      if (selectedEvent) {
        setFormData(prev => {
          // Double-check if already selected to prevent duplicates
          if (!prev.selectedEvents.includes(preSelectedEventId)) {
            return {
              ...prev,
              selectedEvents: [...prev.selectedEvents, preSelectedEventId]
            };
          }
          return prev;
        });

        // Mark as processed
        preSelectedProcessedRef.current = preSelectedEventId;

        // Show toast message
        showToast('success', '已選擇梯次', `${selectedEvent.dateRange} - ${selectedEvent.location}`);
      }
    }
  }, [preSelectedEventId, events]); // 深層連結進站時梯次可能尚未載入，events 到齊後要再試一次

  const handleChange = (field: string, value: any) => {
    let finalValue = value;
    if (field === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length > 4) {
        finalValue = `${digits.slice(0, 4)}-${digits.slice(4, 10)}`;
      } else {
        finalValue = digits;
      }
    }
    setFormData(prev => {
      const next = { ...prev, [field]: finalValue };
      if (field === 'canProvideOfficialSupport' && finalValue !== true) {
        next.officialSupportAvailability = {};
      }
      return next;
    });
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const clearDraft = () => {
    if (window.confirm('確定要清除暫存資料並重新填寫嗎？')) {
      localStorage.removeItem(STORAGE_KEY);
      setFormData(initialData);
      setHasDraft(false);
      setCurrentStep(1);
      showToast('success', '已清除', '表單已重置');
    }
  };

  const toggleEventSelection = (eventId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedEvents.includes(eventId);
      const nextAvailability = { ...(prev.officialSupportAvailability || {}) };
      if (isSelected) {
        delete nextAvailability[eventId];
      }

      return {
        ...prev,
        selectedEvents: isSelected
          ? prev.selectedEvents.filter(id => id !== eventId)
          : [...prev.selectedEvents, eventId],
        officialSupportAvailability: nextAvailability
      };
    });
  };

  const toggleOfficialSupportDay = (eventId: string, dayValue: string) => {
    setFormData(prev => {
      const currentDays = prev.officialSupportAvailability?.[eventId] || [];
      const nextDays = currentDays.includes(dayValue)
        ? currentDays.filter(value => value !== dayValue)
        : [...currentDays, dayValue];

      return {
        ...prev,
        officialSupportAvailability: {
          ...(prev.officialSupportAvailability || {}),
          [eventId]: nextDays
        }
      };
    });

    const errorKey = `officialSupportAvailability.${eventId}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const selectAllOfficialSupportDays = (event: EventOption) => {
    const options = getOfficialSupportDayOptions(event);
    setFormData(prev => ({
      ...prev,
      officialSupportAvailability: {
        ...(prev.officialSupportAvailability || {}),
        [event.id]: options.map(option => option.value)
      }
    }));

    const errorKey = `officialSupportAvailability.${event.id}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (step === 1) {
      if (!formData.email) newErrors.email = "請填寫電子信箱";
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "信箱格式不正確";

      if (!formData.name) newErrors.name = "請填寫姓名";
      if (!formData.church) newErrors.church = "請填寫所屬教會";

      if (!formData.phone) newErrors.phone = "請填寫聯絡電話";
      else if (!/^09\d{2}-\d{6}$/.test(formData.phone)) newErrors.phone = "請輸入正確手機格式 (09xxxxxxxx)";
    }

    if (step === 2) {
      if (!formData.roles || formData.roles.length === 0) {
        newErrors.roles = "請至少選擇一個醫宣角色";
      }
      if (formData.roles && formData.roles.some(r => r.includes('其他專業醫療人員')) && !formData.otherRoleDescription) {
        newErrors.otherRoleDescription = "請說明其他專業身分";
      }
      // 新增：學生角色驗證
      if (formData.roles && formData.roles.includes('學生')) {
        if (!formData.studentSpecialization) {
          newErrors.studentSpecialization = "請選擇您的專業科目";
        } else if (formData.studentSpecialization === '其他（請說明）' && !formData.otherRoleDescription) {
          newErrors.otherRoleDescription = "請說明您的專業科目";
        }
      }
      // 新增：西醫次專科驗證
      if (formData.roles && formData.roles.includes('西醫')) {
        if (!formData.westernMedicineSpecialty) {
          newErrors.westernMedicineSpecialty = "請選擇您的次專科";
        }
      }
      // 治療師次分類驗證
      if (formData.roles && formData.roles.includes('治療師')) {
        if (!formData.therapistSpecialty || !THERAPIST_SPECIALTIES.includes(formData.therapistSpecialty)) {
          newErrors.therapistSpecialty = "請選擇您的治療師類別";
        }
      }
    }

    if (step === 3) {
      if (requiresOfficialSupport && formData.canProvideOfficialSupport === undefined) {
        newErrors.canProvideOfficialSupport = "請選擇是否可報備支援";
      }

      if (requiresOfficialSupport && formData.canProvideOfficialSupport === true) {
        selectedSupportEvents.forEach(event => {
          const options = getOfficialSupportDayOptions(event);
          if (options.length === 0) return;

          const selectedDays = formData.officialSupportAvailability?.[event.id] || [];
          const validSelectedDays = selectedDays.filter(day =>
            options.some(option => option.value === day)
          );

          if (validSelectedDays.length === 0) {
            newErrors[`officialSupportAvailability.${event.id}`] = "請選擇這一梯可報備的義診日";
          }
        });
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Shake effect on the form container
      const formCard = document.getElementById('form-card');
      if (formCard) {
        formCard.classList.remove('animate-shake');
        void formCard.offsetWidth; // trigger reflow
        formCard.classList.add('animate-shake');
      }
      showToast('error', '資料不完整', '請檢查紅色標示的欄位');
    }

    return Object.keys(newErrors).length === 0 && isValid;
  };

  const changeStep = (newStep: number) => {
    setDirection(newStep > currentStep ? 'right' : 'left');
    setIsExiting(true);
    setTimeout(() => {
      setCurrentStep(newStep);
      setIsExiting(false);
    }, 250);
  };

  const checkForDuplicateRegistration = async () => {
    if (!formData.email || !formData.phone) return;

    setIsCheckingDuplicate(true);
    try {
      const getMyRegistration = httpsCallable(functions, 'getMyRegistration');
      const result = await getMyRegistration({
        email: formData.email,
        phone: formData.phone
      });

      const registrations = result.data as Registrant[];
      if (registrations && registrations.length > 0) {
        const existing = registrations[0];
        _setExistingRegistration(existing);
        showToast('info', '歡迎回來！', `${existing.name}，系統已找到您的報名記錄`);
      } else {
        _setExistingRegistration(null);
      }
    } catch (error) {
      console.error('Duplicate check failed:', error);
      // Silently fail - user can still proceed
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      // Check for duplicate after Step 1
      if (currentStep === 1 && !skipDuplicateCheck) {
        await checkForDuplicateRegistration();
      }

      if (currentStep === 3 && formData.selectedEvents.length === 0) {
        if (!window.confirm("您未選擇任何參加梯隊，確定要繼續嗎？")) return;
      }
      changeStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    changeStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!hasAgreed) {
      showToast('info', '請先勾選', '請勾選「我已了解報名與遴選原則，願意配合並順服團契的安排」後再送出。');
      return;
    }
    setIsSubmitting(true);

    try {
      // 檢查選擇的梯隊是否有已截止或已額滿的
      const unavailableEvents = formData.selectedEvents
        .map(eventId => events.find(e => e.id === eventId))
        .filter(evt => evt && (isDeadlinePassed(evt) || evt.registrationClosed));

      if (unavailableEvents.length > 0) {
        const unavailableNames = unavailableEvents
          .map(evt => evt!.dateRange)
          .join('、');
        setIsSubmitting(false);
        showToast(
          'error',
          '報名失敗',
          `以下梯隊已截止或已額滿：${unavailableNames}。請重新選擇。`
        );
        return;
      }

      // 清理和驗證所有輸入
      const validRoles = Object.values(ROLE_CATEGORIES).flat();
      const validDistricts = Object.values(District);
      const validEventIds = events.map(e => e.id);
      const selectedEventIds = validateEvents(formData.selectedEvents, validEventIds);
      const selectedSupportEventIds = new Set(selectedSupportEvents.map(event => event.id));
      const officialSupportAvailability: Record<string, string[]> = {};

      if (requiresOfficialSupport && formData.canProvideOfficialSupport === true) {
        selectedEventIds.forEach(eventId => {
          if (!selectedSupportEventIds.has(eventId)) return;

          const event = events.find(e => e.id === eventId);
          if (!event) return;

          const validDayValues = new Set(getOfficialSupportDayOptions(event).map(option => option.value));
          const selectedDays = formData.officialSupportAvailability?.[eventId] || [];
          const validSelectedDays = selectedDays.filter(day => validDayValues.has(day));

          if (validSelectedDays.length > 0) {
            officialSupportAvailability[eventId] = validSelectedDays;
          }
        });
      }

      const sanitizedData: Omit<Registrant, 'id' | 'submissionDate'> = {
        email: sanitizeEmail(formData.email),
        name: sanitizeName(formData.name),
        gender: validateGender(formData.gender) as Gender,
        church: sanitizeChurch(formData.church),
        district: validateDistrict(formData.district, validDistricts) as District,
        phone: sanitizePhone(formData.phone),
        role: validateRole(formData.role, validRoles),
        roles: formData.roles?.map(r => validateRole(r, validRoles)) || [],
        otherRoleDescription: formData.otherRoleDescription
          ? sanitizeRoleDescription(formData.otherRoleDescription)
          : '',
        studentSpecialization: formData.studentSpecialization || '',
        westernMedicineSpecialty: formData.westernMedicineSpecialty || '',
        therapistSpecialty: formData.therapistSpecialty && THERAPIST_SPECIALTIES.includes(formData.therapistSpecialty)
          ? formData.therapistSpecialty
          : '',
        message: formData.message ? sanitizeMessage(formData.message) : '',
        selectedEvents: selectedEventIds,
        ...(requiresOfficialSupport
          ? {
              canProvideOfficialSupport: formData.canProvideOfficialSupport === true,
              ...(formData.canProvideOfficialSupport === true
                ? { officialSupportAvailability }
                : {})
            }
          : {})
      };

      // ✅ 立即提交，不再延遲
      await onSubmit(sanitizedData);
      localStorage.removeItem(STORAGE_KEY);
      setIsSubmitting(false);

    } catch (error) {
      setIsSubmitting(false);
      if (error instanceof Error) {
        showToast('error', '資料驗證失敗', error.message);
      } else {
        showToast('error', '提交失敗', '請檢查您的輸入資料');
      }
    }
  };

  // Common styling for inputs - Simplified animations
  const inputClasses = (hasError: boolean) =>
    `w-full pl-12 pr-4 py-3 rounded-xl border-2 outline-none transition-colors duration-200 bg-white text-gray-900 placeholder-gray-400
    ${hasError
      ? 'border-red-300 bg-red-50 focus:ring-4 focus:ring-red-100'
      : 'border-gray-300 hover:border-gray-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 focus:bg-white'
    } `;

  const renderStep1 = () => (
    <div className="space-y-8">
      {hasDraft && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex justify-between items-center text-sm text-amber-800 mb-6 animate-fade-in">
          <div className="flex items-center">
            <Save size={16} className="mr-2" />
            <span>已載入上次未完成的資料。</span>
          </div>
          <button
            onClick={clearDraft}
            className="flex items-center text-amber-600 hover:text-amber-900 font-bold underline decoration-2 underline-offset-2 transition-colors"
          >
            <Trash2 size={14} className="mr-1" /> 清除重填
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Email */}
        <div className="col-span-1 md:col-span-2 group">
          <label className="block text-sm font-bold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors">電子信箱 <span className="text-red-500">*</span></label>
          <div className="relative">
            <Mail className={`absolute left-4 top-3.5 transition-colors duration-200 ${errors.email ? 'text-red-400' : 'text-gray-400 group-focus-within:text-indigo-500'} `} size={20} />
            <input
              type="email"
              placeholder="example@gmail.com"
              className={inputClasses(!!errors.email)}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
            {!errors.email && formData.email && /\S+@\S+\.\S+/.test(formData.email) && (
              <div className="text-xs text-indigo-600 mt-1 ml-1 flex items-center font-semibold">
                <CheckCircle size={14} className="mr-1" /> 信箱格式正確
              </div>
            )}
            {errors.email && <p className="text-red-500 text-sm mt-1 ml-1 flex items-center"><AlertTriangle size={14} className="mr-1" />{errors.email}</p>}
          </div>
        </div>

        {/* Name */}
        <div className="group">
          <label className="block text-sm font-bold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors">真實姓名 <span className="text-red-500">*</span></label>
          <div className="relative">
            <User className={`absolute left-4 top-3.5 transition-colors duration-200 ${errors.name ? 'text-red-400' : 'text-gray-400 group-focus-within:text-indigo-500'} `} size={20} />
            <input
              type="text"
              placeholder="王小明"
              className={inputClasses(!!errors.name)}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            {!errors.name && formData.name && formData.name.length >= 2 && (
              <div className="text-xs text-indigo-600 mt-1 ml-1 flex items-center font-semibold">
                <CheckCircle size={14} className="mr-1" /> 已填寫
              </div>
            )}
            {errors.name && <p className="text-red-500 text-sm mt-1 ml-1 flex items-center"><AlertTriangle size={14} className="mr-1" />{errors.name}</p>}
          </div>
        </div>

        {/* Gender */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">性別</label>
          <div className="flex bg-gray-100 p-1.5 rounded-xl">
            {Object.values(Gender).map(g => (
              <button
                key={g}
                onClick={() => handleChange('gender', g)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-colors duration-200 ${formData.gender === g ? 'bg-white text-indigo-700 shadow-md ring-2 ring-indigo-100' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'} `}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Church */}
        <div className="group">
          <label className="block text-sm font-bold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors">所屬教會 <span className="text-red-500">*</span></label>
          <div className="relative">
            <MapPin className={`absolute left-4 top-3.5 transition-colors duration-200 ${errors.church ? 'text-red-400' : 'text-gray-400 group-focus-within:text-indigo-500'} `} size={20} />
            <input
              type="text"
              placeholder="例如：台北教會"
              className={inputClasses(!!errors.church)}
              value={formData.church}
              onChange={(e) => handleChange('church', e.target.value)}
            />
            {!errors.church && formData.church && formData.church.length >= 2 && (
              <div className="text-xs text-indigo-600 mt-1 ml-1 flex items-center font-semibold">
                <CheckCircle size={14} className="mr-1" /> 已填寫
              </div>
            )}
            {errors.church && <p className="text-red-500 text-sm mt-1 ml-1 flex items-center"><AlertTriangle size={14} className="mr-1" />{errors.church}</p>}
          </div>
        </div>

        {/* Phone */}
        <div className="group">
          <label className="block text-sm font-bold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors">聯絡電話 <span className="text-red-500">*</span></label>
          <div className="relative">
            <Phone className={`absolute left-4 top-3.5 transition-colors duration-200 ${errors.phone ? 'text-red-400' : 'text-gray-400 group-focus-within:text-indigo-500'} `} size={20} />
            <input
              type="text"
              placeholder="09xxxxxxxx"
              maxLength={11}
              className={inputClasses(!!errors.phone)}
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
            {!errors.phone && formData.phone && formData.phone.length > 0 && (
              <div className="text-xs text-gray-500 mt-1 ml-1 flex items-center">
                {formData.phone.match(/^09\d{2}-\d{6}$/) ? (
                  <span className="text-indigo-600 flex items-center font-semibold">
                    <CheckCircle size={14} className="mr-1" /> 格式正確
                  </span>
                ) : (
                  <span className="text-amber-600">請輸入 10 碼手機號碼（09 開頭）</span>
                )}
              </div>
            )}
            {errors.phone && <p className="text-red-500 text-sm mt-1 ml-1 flex items-center"><AlertTriangle size={14} className="mr-1" />{errors.phone}</p>}
          </div>
        </div>

        {/* District */}
        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-bold text-gray-700 mb-3">所屬教區</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.values(District).map(d => (
              <button
                key={d}
                onClick={() => handleChange('district', d)}
                className={`py-3 px-3 rounded-xl border-2 text-sm font-bold transition-colors duration-200 ${formData.district === d ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md ring-2 ring-blue-100' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'} `}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="bg-blue-50 p-5 rounded-2xl flex items-start border border-blue-100 shadow-sm">
        <Info className="text-blue-600 mt-0.5 mr-3 flex-shrink-0" size={20} />
        <p className="text-sm text-blue-900 font-medium leading-relaxed">
          請選擇您在醫宣中主要擔任的角色。若您是學生，請選擇對應科系。
        </p>
      </div>

      {Object.entries(ROLE_CATEGORIES).map(([category, roles]) => (
        <div key={category} className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 border-l-4 border-blue-500 pl-3">{category}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {roles.map(role => {
              const cleanName = role.split('~')[0].split(',')[0];
              const isSelected = formData.roles?.includes(role) || false;
              return (
                <button
                  key={role}
                  onClick={() => {
                    const currentRoles = formData.roles || [];
                    let newRoles: string[];
                    if (isSelected) {
                      // 取消選擇
                      newRoles = currentRoles.filter(r => r !== role);
                    } else {
                      // 添加選擇
                      newRoles = [...currentRoles, role];
                    }
                    const nextRequiresOfficialSupport = newRoles.some(isOfficialSupportRole);

                    // 更新 roles 和 role（role 保持為第一個選擇的角色）
                    setFormData(prev => ({
                      ...prev,
                      roles: newRoles,
                      role: newRoles[0] || '',
                      canProvideOfficialSupport: nextRequiresOfficialSupport ? prev.canProvideOfficialSupport : undefined,
                      officialSupportAvailability: nextRequiresOfficialSupport ? prev.officialSupportAvailability : {}
                    }));
                  }}
                  className={`flex items-center p-4 rounded-xl border-2 text-left transition-colors duration-200 group relative overflow-hidden ${isSelected
                    ? 'border-blue-600 bg-blue-600 text-white shadow-lg ring-2 ring-blue-200'
                    : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                    } `}
                >
                  <div className={`w-6 h-6 rounded-lg border-2 mr-3 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${isSelected ? 'border-white bg-white/20' : 'border-gray-300 group-hover:border-blue-400'} `}>
                    {isSelected && (
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="font-bold text-base">{cleanName}</span>
                </button>
              )
            })}
          </div>
        </div>
      ))}

      {/* 學生專業科目選擇 */}
      {formData.roles && formData.roles.includes('學生') && (
        <div className="animate-fade-in mt-6">
          <label className="block text-sm font-bold text-gray-700 mb-3">您是哪個科系的學生？ <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {STUDENT_SPECIALIZATIONS.map(spec => {
              const isSelected = formData.studentSpecialization === spec;
              return (
                <button
                  key={spec}
                  onClick={() => handleChange('studentSpecialization', spec)}
                  className={`p-3 rounded-xl border-2 text-sm font-bold transition-colors duration-200 ${
                    isSelected
                      ? 'border-blue-600 bg-blue-600 text-white shadow-lg'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                  }`}
                >
                  {spec}
                </button>
              );
            })}
          </div>
          {errors.studentSpecialization && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertTriangle size={14} className="mr-1" />
              {errors.studentSpecialization}
            </p>
          )}
        </div>
      )}

      {/* 西醫次專科選擇 */}
      {formData.roles && formData.roles.includes('西醫') && (
        <div className="animate-fade-in mt-6">
          <label className="block text-sm font-bold text-gray-700 mb-3">您的次專科是？ <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {WESTERN_MEDICINE_SPECIALTIES.map(specialty => {
              const isSelected = formData.westernMedicineSpecialty === specialty;
              return (
                <button
                  key={specialty}
                  onClick={() => handleChange('westernMedicineSpecialty', specialty)}
                  className={`p-3 rounded-xl border-2 text-sm font-bold transition-colors duration-200 ${
                    isSelected
                      ? 'border-blue-600 bg-blue-600 text-white shadow-lg'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                  }`}
                >
                  {specialty}
                </button>
              );
            })}
          </div>
          {errors.westernMedicineSpecialty && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertTriangle size={14} className="mr-1" />
              {errors.westernMedicineSpecialty}
            </p>
          )}
        </div>
      )}

      {/* 治療師次分類選擇 */}
      {formData.roles && formData.roles.includes('治療師') && (
        <div className="animate-fade-in mt-6">
          <label className="block text-sm font-bold text-gray-700 mb-3">您的治療師類別是？ <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {THERAPIST_SPECIALTIES.map(specialty => {
              const isSelected = formData.therapistSpecialty === specialty;
              return (
                <button
                  key={specialty}
                  onClick={() => handleChange('therapistSpecialty', specialty)}
                  className={`p-3 rounded-xl border-2 text-sm font-bold transition-colors duration-200 ${
                    isSelected
                      ? 'border-blue-600 bg-blue-600 text-white shadow-lg'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                  }`}
                >
                  {specialty}
                </button>
              );
            })}
          </div>
          {errors.therapistSpecialty && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertTriangle size={14} className="mr-1" />
              {errors.therapistSpecialty}
            </p>
          )}
        </div>
      )}

      {/* 其他專業說明 */}
      {((formData.roles && formData.roles.some(r => r.includes('其他專業醫療人員'))) ||
        (formData.studentSpecialization === '其他（請說明）')) && (
        <div className="animate-fade-in mt-4">
          <label className="block text-sm font-bold text-gray-700 mb-2">請說明您的專業身分 <span className="text-red-500">*</span></label>
          <input
            type="text"
            autoFocus
            placeholder="例如：醫管系、牙技師..."
            className={inputClasses(!!errors.otherRoleDescription)}
            value={formData.otherRoleDescription}
            onChange={(e) => handleChange('otherRoleDescription', e.target.value)}
          />
          {errors.otherRoleDescription && <p className="text-red-500 text-sm mt-1 animate-fade-in">{errors.otherRoleDescription}</p>}
        </div>
      )}

    </div>
  );

  const renderOfficialSupportFields = () => (
    <div className="animate-fade-in mt-6">
      <div className="bg-amber-50 p-5 rounded-2xl border-2 border-amber-200 mb-4">
        <div className="flex items-start">
          <Info className="text-amber-600 mt-0.5 mr-3 flex-shrink-0" size={20} />
          <div className="text-sm text-amber-800 leading-relaxed">
            <p className="font-bold mb-1">專業醫療人員報備支援說明</p>
            <p>您選擇的梯隊包含義診活動，請告知是否可以向所屬單位報備支援。</p>
          </div>
        </div>
      </div>

      <label className="block text-sm font-bold text-gray-700 mb-3">報備支援狀態 <span className="text-red-500">*</span></label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => handleChange('canProvideOfficialSupport', true)}
          className={`p-4 rounded-xl border-2 text-left transition-colors duration-200 ${
            formData.canProvideOfficialSupport === true
              ? 'border-green-600 bg-green-600 text-white shadow-lg ring-2 ring-green-200'
              : 'bg-white border-gray-200 hover:border-green-300 hover:bg-green-50 text-gray-700'
          }`}
        >
          <div className="flex items-center">
            <div className={`w-6 h-6 rounded-lg border-2 mr-3 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
              formData.canProvideOfficialSupport === true ? 'border-white bg-white/20' : 'border-gray-300'
            }`}>
              {formData.canProvideOfficialSupport === true && (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div>
              <div className="font-bold text-base">可報備支援</div>
              <div className={`text-xs mt-1 ${formData.canProvideOfficialSupport === true ? 'text-white/80' : 'text-gray-500'}`}>
                可向單位報備參與義診
              </div>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => handleChange('canProvideOfficialSupport', false)}
          className={`p-4 rounded-xl border-2 text-left transition-colors duration-200 ${
            formData.canProvideOfficialSupport === false
              ? 'border-gray-600 bg-gray-600 text-white shadow-lg ring-2 ring-gray-200'
              : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
          }`}
        >
          <div className="flex items-center">
            <div className={`w-6 h-6 rounded-lg border-2 mr-3 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
              formData.canProvideOfficialSupport === false ? 'border-white bg-white/20' : 'border-gray-300'
            }`}>
              {formData.canProvideOfficialSupport === false && (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div>
              <div className="font-bold text-base">無報備支援</div>
              <div className={`text-xs mt-1 ${formData.canProvideOfficialSupport === false ? 'text-white/80' : 'text-gray-500'}`}>
                無法向單位報備
              </div>
            </div>
          </div>
        </button>
      </div>
      {errors.canProvideOfficialSupport && (
        <p className="text-red-500 text-sm mt-2 flex items-center">
          <AlertTriangle size={14} className="mr-1" />
          {errors.canProvideOfficialSupport}
        </p>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">請點選您要參加的梯隊 ({year})</h3>
        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-bold">
          已選擇 {formData.selectedEvents.length} 場
        </span>
      </div>

      {events.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-200 p-8 rounded-2xl text-center text-gray-400">
          目前無 {year} 年度可選梯隊
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((evt) => {
            const isSelected = formData.selectedEvents.includes(evt.id);
            const isPassed = isDeadlinePassed(evt);
            const isClosed = evt.registrationClosed;
            const deadlineStatus = getDeadlineStatus(evt);
            const isDisabled = isPassed || isClosed;

            return (
              <button
                key={evt.id}
                onClick={() => !isDisabled && toggleEventSelection(evt.id)}
                disabled={isDisabled}
                className={`relative p-5 rounded-2xl border-2 transition-colors duration-200 flex flex-col text-left h-full group ${
                  isDisabled
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : isSelected
                    ? 'border-blue-600 bg-blue-50 shadow-lg ring-4 ring-blue-100'
                    : 'border-gray-400 bg-gradient-to-br from-gray-50 to-white shadow-md hover:border-blue-400 hover:shadow-lg'
                }`}
              >
                {/* Enhanced selection indicator */}
                {isSelected && !isDisabled && (
                  <div className="absolute top-0 right-0 bg-gradient-to-br from-blue-700 to-blue-600 rounded-tr-2xl rounded-bl-2xl px-3 py-2 shadow-md">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle size={18} className="text-white" strokeWidth={2.5} />
                      <span className="text-white text-xs font-bold">已選擇</span>
                    </div>
                  </div>
                )}

                {isDisabled && (
                  <div className="absolute top-3 right-3 text-gray-400">
                    <Ban size={24} />
                  </div>
                )}

                <div className={`mb-3 ${isSelected ? 'pr-20' : ''}`}>
                  <div className={`text-lg font-extrabold ${
                    isDisabled ? 'text-gray-500' :
                    isSelected ? 'text-blue-800' : 'text-gray-900'
                  }`}>
                    {evt.dateRange}
                  </div>
                  <div className={`text-sm font-medium mt-1 ${
                    isSelected ? 'text-blue-600' : 'text-gray-700'
                  }`}>
                    {evt.location}
                  </div>
                </div>

                <div className="mt-auto pt-3 border-t border-dashed border-gray-200 w-full">
                  <div className="flex justify-between items-center">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${
                      isClosed
                        ? 'text-orange-600 bg-orange-100'
                        : isPassed
                        ? 'text-gray-500 bg-gray-100'
                        : deadlineStatus.type === 'urgent'
                        ? 'text-red-700 bg-red-100 border border-red-200'
                        : 'text-red-600 bg-red-50'
                    }`}>
                      {isClosed ? '已額滿' : isPassed ? '已截止' : `截止: ${evt.deadline}`}
                    </span>
                  </div>
                </div>

                {/* Urgent deadline warning */}
                {!isPassed && deadlineStatus.type === 'urgent' && !isSelected && (
                  <div className="mt-2 text-xs text-red-600 font-semibold bg-red-50 px-2 py-1 rounded border border-red-100">
                    ⚠️ {deadlineStatus.message}，即將截止！
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {requiresOfficialSupport && renderOfficialSupportFields()}

      {requiresOfficialSupport && formData.canProvideOfficialSupport === true && selectedSupportEvents.length > 0 && (
        <div className="mt-8 pt-6 border-t border-amber-200 space-y-4">
          <div>
            <h3 className="text-lg font-bold text-amber-900">可報備日期</h3>
            <p className="text-sm text-amber-800 mt-1">
              請依每一梯義診選擇可向單位報備支援的日期。
            </p>
          </div>

          <div className="space-y-4">
            {selectedSupportEvents.map(event => {
              const dayOptions = getOfficialSupportDayOptions(event);
              const selectedDays = formData.officialSupportAvailability?.[event.id] || [];
              const allSelected = dayOptions.length > 0 && dayOptions.every(option => selectedDays.includes(option.value));
              const errorKey = `officialSupportAvailability.${event.id}`;

              return (
                <div
                  key={event.id}
                  data-testid={`official-support-days-${event.id}`}
                  className={`rounded-2xl border-2 p-4 bg-white ${errors[errorKey] ? 'border-red-300 bg-red-50/50' : 'border-amber-200'}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div>
                      <div className="font-bold text-gray-900">{event.dateRange}</div>
                      <div className="text-sm text-gray-500">{event.location}</div>
                    </div>
                    {dayOptions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => selectAllOfficialSupportDays(event)}
                        className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-colors whitespace-nowrap ${
                          allSelected
                            ? 'border-green-600 bg-green-600 text-white shadow'
                            : 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
                        }`}
                      >
                        都可
                      </button>
                    )}
                  </div>

                  {dayOptions.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {dayOptions.map(option => {
                        const isSelected = selectedDays.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => toggleOfficialSupportDay(event.id, option.value)}
                            className={`min-h-12 px-4 py-3 rounded-xl border-2 text-left font-bold transition-colors ${
                              isSelected
                                ? 'border-green-600 bg-green-50 text-green-800 ring-2 ring-green-100'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50'
                            }`}
                          >
                            <span className="flex items-center">
                              <span className={`w-5 h-5 rounded-md border-2 mr-2 flex items-center justify-center shrink-0 ${
                                isSelected ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300'
                              }`}>
                                {isSelected && <CheckCircle size={14} />}
                              </span>
                              {option.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                      這一梯尚未設定可辨識的義診日期，請在備註補充可報備時間。
                    </div>
                  )}

                  {errors[errorKey] && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <AlertTriangle size={14} className="mr-1" />
                      {errors[errorKey]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-gray-200">
        <label className="block text-sm font-bold text-gray-700 mb-2">備註（選填）</label>
        <textarea
          rows={3}
          placeholder="任何建議或備註事項..."
          className={`${inputClasses(false)} resize-none`}
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      <label className="flex items-start gap-3 bg-white p-5 rounded-2xl border border-indigo-100 shadow-sm cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5 accent-indigo-600 flex-shrink-0"
          checked={hasAgreed}
          onChange={(e) => setHasAgreed(e.target.checked)}
        />
        <span className="text-gray-700 leading-relaxed">
          <span className="block font-bold text-gray-800">我已了解報名與遴選原則，願意配合並順服團契的安排。</span>
        </span>
      </label>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 font-bold text-gray-700">
          基本資料
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm md:text-base">
          <div>
            <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wide">姓名</span>
            <span className="font-bold text-gray-900 text-lg">{formData.name}</span>
          </div>
          <div>
            <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wide">教會 / 教區</span>
            <span className="font-bold text-gray-900">{formData.church} ({formData.district})</span>
          </div>
          <div>
            <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wide">聯絡電話</span>
            <span className="font-bold text-gray-900 font-mono">{formData.phone}</span>
          </div>
          <div>
            <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wide">電子信箱</span>
            <span className="font-bold text-gray-900 break-all">{formData.email}</span>
          </div>
          <div className="md:col-span-2">
            <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wide">醫宣角色</span>
            <div className="flex flex-wrap gap-2">
              {(formData.roles && formData.roles.length > 0 ? formData.roles : [formData.role]).map((role, idx) => (
                <span key={idx} className="font-bold text-indigo-700 bg-indigo-50 px-3 py-1 rounded-lg inline-block border border-indigo-100">
                  {role.split('~')[0]}
                  {role === '學生' && formData.studentSpecialization && (
                    <span className="ml-1 text-indigo-600">({formData.studentSpecialization})</span>
                  )}
                  {role === '西醫' && formData.westernMedicineSpecialty && (
                    <span className="ml-1 text-indigo-600">({formData.westernMedicineSpecialty})</span>
                  )}
                  {role === '治療師' && formData.therapistSpecialty && (
                    <span className="ml-1 text-indigo-600">({formData.therapistSpecialty})</span>
                  )}
                </span>
              ))}
              {formData.otherRoleDescription && (
                <span className="text-gray-600 bg-gray-50 px-3 py-1 rounded-lg inline-block border border-gray-200">
                  {formData.studentSpecialization === '其他（請說明）' ? '專業：' : '其他：'}{formData.otherRoleDescription}
                </span>
              )}
            </div>
          </div>
          {requiresOfficialSupport && (
            <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <span className="text-amber-800 block mb-1 text-xs font-bold uppercase tracking-wide">報備支援狀態</span>
              <span className={`font-bold ${formData.canProvideOfficialSupport ? 'text-green-700' : 'text-gray-700'}`}>
                {formData.canProvideOfficialSupport ? '可報備支援' : '無報備支援'}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 font-bold text-gray-700 flex justify-between items-center">
          <span>參加梯隊 ({year})</span>
          <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-bold">
            共 {formData.selectedEvents.length} 場
          </span>
        </div>
        <div className="p-0">
          {formData.selectedEvents.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {formData.selectedEvents.map(id => {
                const ev = events.find(e => e.id === id);
                const supportAvailability = ev
                  ? formatOfficialSupportAvailability(ev, formData.officialSupportAvailability?.[id] || [])
                  : '';
                return (
                  <div key={id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="font-bold text-gray-800">{ev?.dateRange}</span>
                      <span className="text-gray-500 text-sm">{ev?.location}</span>
                    </div>
                    {requiresOfficialSupport && formData.canProvideOfficialSupport === true && supportAvailability && (
                      <div className="mt-2 inline-flex max-w-full items-center rounded-lg bg-green-50 border border-green-200 px-3 py-1 text-sm font-bold text-green-800">
                        報備日期：{supportAvailability}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400 italic">
              <AlertTriangle size={24} className="mx-auto mb-2 opacity-50" />
              本次未報名任何梯隊
            </div>
          )}
        </div>
      </div>

      {formData.message && (
        <div className="bg-gray-50 p-4 rounded-xl text-gray-600 text-sm italic border border-gray-200">
          <span className="font-semibold text-gray-700">備註：</span>{formData.message}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* 全屏 Loading Overlay - Enhanced with micro-interactions */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md mx-4 animate-bounce-fade-in">
            <div className="flex flex-col items-center">
              <div className="relative">
                <Loader2 size={48} className="text-indigo-600 animate-spin mb-4" />
                <div className="absolute inset-0 bg-indigo-300 rounded-full blur-xl opacity-50 animate-pulse"></div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 animate-pulse">正在送出報名...</h3>
              <div className="mt-4 flex space-x-1">
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce delay-200"></div>
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce delay-300"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
      {/* --- IMPORTANT NOTICE --- */}
      <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-5 shadow-lg animate-fade-in">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-md">
              <Info size={24} className="text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center">
              報名須知
            </h3>
            <p className="text-amber-800 font-semibold leading-relaxed">
              若您有心參與醫宣服事，歡迎先行報名。醫事同工若尚未確定排班或請假安排，也可先報名，以免等候排班確認後，已錯過報名期限。
            </p>
            <p className="text-amber-700 text-sm mt-2 leading-relaxed">
              報名後若確認無法參加，請儘早取消報名，讓團契能及時安排其他同工。若已超過線上取消期限，請聯絡醫宣幹部協助辦理。
            </p>
            <p className="text-amber-700 text-sm mt-2 leading-relaxed">
              遴選時，會優先考量曾參加醫宣動員聚會的同工、承辦教會當地同工，以及在期限內完成報名的同工，並依各科開診與服事需要、報名順序綜合安排。
            </p>
            <p className="text-amber-700 text-sm mt-2 leading-relaxed">
              逾期仍有意參與的同工，請洽醫宣幹部登記替補；若經遴選錄取，會另行通知。
            </p>
            <p className="text-amber-700 text-sm mt-2 leading-relaxed">
              各梯次所需人力不同，承辦教會的接待人數也有限。最終遴選與服事安排，由醫宣幹部群在禱告中審慎決定。也請同工彼此體諒，配合並順服團契的安排，一同為醫宣服事禱告。
            </p>
          </div>
        </div>
      </div>

      {/* --- PROGRESS HEADER --- */}
      <div className="mb-8">
        {/* Progress bar with percentage */}
        <div className="mb-6 px-2">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-bold text-gray-600">完成進度</span>
            <span className="text-sm font-bold text-indigo-600">{Math.round((currentStep / STEPS.length) * 100)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 transition-all duration-300 ease-out shadow-sm"
              style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mb-4 px-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            return (
              <div key={step.id} className="flex flex-col items-center relative z-10 w-1/4">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 mb-2 shadow-sm
                    ${isActive ? 'bg-indigo-600 text-white shadow-lg ring-4 ring-indigo-100' :
                      isCompleted ? 'bg-indigo-500 text-white shadow-md' : 'bg-gray-200 text-gray-400'
                    }
`}
                >
                  {isCompleted ? <CheckCircle size={20} /> : <Icon size={20} />}
                </div>
                <span className={`text-xs md:text-sm font-bold transition-colors duration-300 ${isActive ? 'text-indigo-700' : 'text-gray-400'} `}>
                  {step.title}
                </span>
                {index < STEPS.length - 1 && (
                  <div className="absolute top-5 left-1/2 w-full h-[3px] -z-10 rounded-full overflow-hidden">
                    <div className={`h-full w-full transition-all duration-500 ${isCompleted ? 'bg-indigo-500' : 'bg-gray-200'} `}></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* --- MAIN FORM CARD --- */}
      <div id="form-card" className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden min-h-[500px] flex flex-col transition-transform duration-200">
        <div className="p-6 md:p-10 flex-grow overflow-hidden">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center">
            {STEPS[currentStep - 1].title}
            <span className="ml-auto text-sm font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Step {currentStep}/4</span>
          </h2>

          <div
            className={`transition-all duration-300 ease-out transform
              ${isExiting
                ? (direction === 'right' ? '-translate-x-8 opacity-0' : 'translate-x-8 opacity-0')
                : 'translate-x-0 opacity-100'
              } `}
          >
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </div>
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-between items-center sticky bottom-0 backdrop-blur-sm bg-gray-50/90 z-20">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center px-6 py-3 rounded-xl text-gray-600 font-bold hover:bg-gray-200 transition-colors duration-200 ${currentStep === 1 ? 'opacity-0 pointer-events-none' : ''} `}
          >
            <ArrowLeft size={20} className="mr-2" /> 上一步
          </button>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              className="flex items-center px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg transition-colors duration-200"
            >
              <span>下一步</span>
              <ArrowRight size={20} className="ml-2" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !hasAgreed}
              title={!hasAgreed ? '請先勾選「我已了解報名與遴選原則，願意配合並順服團契的安排」' : undefined}
              className={`flex items-center px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg transition-colors duration-200 text-lg ${isSubmitting || !hasAgreed ? 'opacity-60 cursor-not-allowed' : ''} `}
            >
              {isSubmitting ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Send size={20} className="mr-2" />}
              <span>{isSubmitting ? '處理中...' : '確認送出'}</span>
            </button>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default RegistrationForm;
